import { createSelector } from 'reselect'
import type { ActionRestrictions, Group, GroupLockSettings } from 'types/group'
import type { Playlist, PlaylistId } from 'types/playlist'
import type { State } from 'types/redux'
import type { Track, TrackId, WriterSplit } from 'types/track'
import {
  INSTANT_INSTRUMENTAL,
  INSTANT_INSTRUMENTAL_HIDDEN_OVERRIDE,
  INSTANT_INSTRUMENTAL_VISIBLE,
} from '@disco/constants/FeatureTypes'
import { WATERMARKED } from '@disco/constants/WatermarkStatusesTypes'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { getGroups } from '@disco/state-management/selectors/groups'
import {
  convertGroupStringsToGroupIds,
  getGroupsRestrictions,
  groupsContainLockedAndRegularChannels,
} from '@disco/utils/groups'
/**
 * Gets all tracks from the state.
 * @param state - The Redux state
 * @returns The tracks object keyed by track ID
 */
export const getTracks = (state: State): State['tracks'] => state.tracks
/**
 * Gets a specific track by ID.
 * @param state - The Redux state
 * @param props - Object containing the track ID
 * @param props.id - The track ID to retrieve
 * @returns The track object, or null/undefined if not found
 */
export const getTrack = (
  state: State,
  props: {
    id: TrackId
  },
): Track | null | undefined => state.tracks[props.id]
/**
 * Gets a specific playlist by ID.
 * This is a properly typed clone of the getPlaylist selector, copy-pasted to evade circular dependencies.
 * @param state - The Redux state
 * @param props - Object containing the playlist ID
 * @param props.playlistId - The playlist ID to retrieve
 * @returns The playlist object, or null/undefined if not found
 * @note Typing selector in selectors/playlists will require more work as it's used on public pages
 */
export const getPlaylist = (
  state: State,
  {
    playlistId,
  }: {
    playlistId: PlaylistId
  },
): Playlist | null | undefined => state.playlists[playlistId]
/**
 * Gets multiple tracks by their IDs.
 * @param state - The Redux state
 * @param props - Object containing array of track IDs
 * @param props.ids - Array of track IDs to retrieve
 * @returns Array of track objects (may contain null/undefined for missing tracks)
 */
export const getTracksByIds = createSelector(
  getTracks,
  (_state, { ids }) => ids,
  (tracks: ReturnType<typeof getTracks>, ids: TrackId[]) =>
    ids.map(id => tracks[id]),
)
/**
 * Gets all tracks that have a specific nesting parent ID.
 * @param state - The Redux state
 * @param props - Object containing the parent ID
 * @param props.id - The nesting parent ID to filter by
 * @returns Array of tracks that are nested under the specified parent
 */
export const getTracksByNestingParentId = createSelector(
  getTracks,
  (_state, { id }) => id,
  (tracks: ReturnType<typeof getTracks>, id) =>
    Object.values<Track>(tracks).filter((track: Track) => {
      return track.nesting_parent_id === Number(id)
    }),
)

/**
 * Checks if a track is editable by the current user.
 * @param state - The Redux state
 * @param props - Object containing the track ID
 * @param props.id - The track ID to check edit permissions for
 * @returns True if the track can be managed by the user, false otherwise
 */
export const getEditableTrackById = createSelector(
  getTracks,
  (_state, { id }) => id,
  (tracks: ReturnType<typeof getTracks>, id: TrackId) => {
    const track = tracks[id]
    if (!track) return null
    return track?.permissions?.manage
  },
)

/**
 * Gets track IDs for tracks that have a specific nesting parent ID.
 * @param state - The Redux state
 * @param props - Object containing the parent ID
 * @param props.id - The nesting parent ID to filter by
 * @returns Array of track IDs that are nested under the specified parent
 */
export const getTrackIdsByNestingParentId = createSelector(
  getTracksByNestingParentId,
  (_state, { id }) => id,
  (tracks: ReturnType<typeof getTracksByNestingParentId>) =>
    tracks.map(track => track.id),
)
/**
 * Gets tracks from a playlist with full track objects.
 * @param state - The Redux state
 * @param props - Object containing the playlist ID
 * @param props.playlistId - The playlist ID to get tracks from
 * @returns Array of track objects from the playlist
 */
export const getAdjancentPlaylistTracks = createSelector(
  [getTracks, getPlaylist],
  (tracks, playlist) => {
    if (!playlist?.tracks) return []
    // @ts-expect-error todo fix later
    return playlist.tracks.map(id => tracks[id]).filter(Boolean)
  },
)
/**
 * Gets tracks from a shortlist with full track objects.
 * @param state - The Redux state
 * @param props - Object containing the shortlist ID
 * @param props.shortlistId - The shortlist ID to get tracks from
 * @returns Array of track objects from the shortlist
 */
export const getAdjancentShortlistTracks = createSelector(
  [
    getTracks,
    (state: State, props: { shortlistId: string }) =>
      state.shortlists.byId[props.shortlistId],
  ],
  (tracks, shortlist) => {
    if (!shortlist?.items) return []
    return shortlist.items.map(item => tracks[item.track.id]).filter(Boolean)
  },
)

/**
 * Gets action restrictions for a track based on its related playlists.
 * @param state - The Redux state
 * @param props - Object containing the track ID
 * @param props.id - The track ID to get restrictions for
 * @returns Action restrictions object indicating what actions are disabled
 */
const getRelatedPlaylistsRestrictions: (
  state: State,
  props: {
    id: TrackId
  },
) => ActionRestrictions = createSelector(
  [getTrack, (state: State) => state.playlists, getGroups],
  (track, playlists, groups) => {
    // track is null in shortlists durign upload
    if (!track)
      return {
        disableDelete: false,
      }
    // track playlists are loaded only if user opened track details,
    // we can't 100% rely on that
    const relatedPlaylists = [...(track.playlists ?? [])]
    Object.keys(playlists).forEach(key => {
      const id = parseInt(key, 10)
      const playlist = playlists[id]
      if (
        playlist &&
        (playlist.tracks ?? []).includes(track.id) &&
        !relatedPlaylists.includes(id)
      )
        relatedPlaylists.push(id)
    })
    const relatedRestrictions = relatedPlaylists.reduce<{
      lock_content?: boolean | null
    }>((acc, pId) => {
      const playlist = playlists[pId]
      if (!playlist) return acc
      const playlistGroups = convertGroupStringsToGroupIds(playlist.groups)
      const restrictions = getGroupsRestrictions(groups, playlistGroups)
      return {
        lock_content: acc.lock_content || restrictions?.lock_content,
      }
    }, {})
    return {
      disableDelete: relatedRestrictions?.lock_content ?? false,
    }
  },
)

/**
 * Gets lock settings for a track based on its associated groups.
 * @param track - The track object
 * @param groups - Array of group objects
 * @returns Group lock settings for the track
 */
const getTrackLockSettings = (
  track: Track,
  groups: Group[],
): GroupLockSettings => {
  const trackGroups = convertGroupStringsToGroupIds(track.groups)
  return getGroupsRestrictions(groups, trackGroups)
}

/**
 * Gets action restrictions for a track based on group locks and related playlists.
 * @param state - The Redux state
 * @param props - Object containing the track ID
 * @param props.id - The track ID to get restrictions for
 * @returns Action restrictions object indicating what actions are disabled
 */
const getTrackRestrictions: (
  state: State,
  props: {
    id: TrackId
  },
) => ActionRestrictions = createSelector(
  [getTrack, getRelatedPlaylistsRestrictions, getGroups],
  (track, relatedRestrictions, groups) => {
    // console.count('getTrackRestrictions')
    const locks = track ? getTrackLockSettings(track, groups) : null
    return {
      disableDelete:
        relatedRestrictions.disableDelete || (locks?.lock_content ?? false),
    }
  },
)
/**
 * Gets all tracks that have watermarking applied.
 * @param state - The Redux state
 * @returns Array of watermarked track objects
 */
export const getWatermarkedTracks: (state: State) => Track[] = createSelector(
  [getTracks],
  (tracks: Record<number, Track>) => {
    return Object.values(tracks).filter(
      (t: Track) => t.watermarking_status === WATERMARKED,
    )
  },
)
/**
 * Factory function that creates a selector to get track restrictions.
 * @returns A selector function for getting track restrictions
 */
export const makeGetTrackRestrictions = (): typeof getTrackRestrictions =>
  getTrackRestrictions
/**
 * Checks if a locked track belongs to regular (non-locked) groups.
 * @param state - The Redux state
 * @param props - Object containing the track ID
 * @param props.id - The track ID to check
 * @returns True if the locked track belongs to regular groups, false otherwise
 */
const getIfLockedTrackBelongsToRegularGroups: (
  state: State,
  props: {
    id: TrackId
  },
) => boolean = createSelector([getTrack, getGroups], (track, groups) => {
  if (!track) return false
  const trackGroups = convertGroupStringsToGroupIds(track.groups)
  return groupsContainLockedAndRegularChannels(groups, trackGroups)
})
/**
 * Factory function that creates a selector to check if a locked track belongs to regular groups.
 * @returns A selector function for checking locked track group membership
 */
export const makeGetIfLockedTrackBelongsToRegularGroups =
  (): typeof getIfLockedTrackBelongsToRegularGroups =>
    getIfLockedTrackBelongsToRegularGroups
/**
 * Gets the IDs of tracks that are allowed to be removed (not locked).
 * @param state - The Redux state
 * @param props - Object containing array of track IDs to check
 * @param props.ids - Array of track IDs to filter
 * @returns Array of track IDs that can be removed
 */
export const getTrackIdsAllowedForRemoval: (
  state: State,
  props: {
    ids: TrackId[]
  },
) => TrackId[] = createSelector(
  [getTracksByIds, getGroups],
  (tracks, groups) => {
    const result = tracks
      .filter(track => {
        if (!track) return false
        const locks = getTrackLockSettings(track, groups)
        return !locks?.lock_content
      })
      .map(t => t.id)
    return result
  },
)
/**
 * Checks if the instant instrumental feature is visible to the user.
 * @param state - The Redux state
 * @returns True if instant instrumental feature should be visible
 */
export const isInstantInstrumentalVisible = createSelector(
  [
    (state: State) =>
      isFeatureEnabled(state, {
        featureName: INSTANT_INSTRUMENTAL_HIDDEN_OVERRIDE,
      }),
    (state: State) =>
      isFeatureEnabled(state, { featureName: INSTANT_INSTRUMENTAL_VISIBLE }),
    (state: State) =>
      isFeatureEnabled(state, { featureName: INSTANT_INSTRUMENTAL }),
  ],
  (isHidden, isVisible, isEnabled) => {
    return isEnabled || (isVisible && !isHidden)
  },
)
const EMPTY_ARRAY = []
/**
 * Gets writer splits for a specific track.
 * @param state - The Redux state
 * @param props - Object containing the track ID
 * @returns Array of writer split objects, or empty array if no splits
 */
export const getTrackWriterSplits = (
  state: State,
  props: any,
): WriterSplit[] => {
  const track = getTrack(state, props)
  return track?.track_writers ? track.track_writers : EMPTY_ARRAY
}
// TODO: Remove temporary alias in future
export {
  getNestedTracksCount,
  getStemsCount,
  getVersionsCount,
} from '@disco/utils/tracks'
